import request from '@/plugins/request';
import { getApiUrl } from '@/helpers';
import type { AxiosResponse } from 'axios';

/**
 * Labels are served by TL-OES (see docs/superpowers/specs/2026-09-15-shipping-label-storage-design.md)
 * so the shipping station never downloads from ShipStation. UHB prints the bytes as a data URL.
 */

export function labelPdfUrl(shippingLabelId: number, combined = false): string {
  return getApiUrl(`/api/shipping/labels/${shippingLabelId}/pdf${combined ? '?combined=true' : ''}`);
}

export class LabelPdfUnavailableError extends Error {
  status: number;
  serverMessage: string;
  constructor(status: number, serverMessage: string) {
    super(serverMessage || `Label request failed with status ${status}`);
    this.name = 'LabelPdfUnavailableError';
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

/** 502: our backfill failed right now. 415: not a PDF label, never stored. Both keep today's direct print. */
export function shouldFallBackToDirectUrl(error: unknown): boolean {
  return error instanceof LabelPdfUnavailableError && (error.status === 502 || error.status === 415);
}

async function readServerMessage(blob: unknown): Promise<string> {
  try {
    if (blob instanceof Blob) {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      return typeof parsed?.error === 'string' ? parsed.error : text;
    }
  } catch {
    /* not JSON */
  }
  return '';
}

export async function fetchLabelAsDataUrl(shippingLabelId: number, combined = false): Promise<string> {
  let response: AxiosResponse<Blob>;
  try {
    response = await request.get(`/api/shipping/labels/${shippingLabelId}/pdf`, {
      params: combined ? { combined: true } : undefined,
      responseType: 'blob',
      validateStatus: (s) => s === 200
    });
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    throw new LabelPdfUnavailableError(status, await readServerMessage(err?.response?.data));
  }
  const contentType = String(response.headers['content-type'] || '');
  if (!contentType.startsWith('application/pdf')) {
    throw new LabelPdfUnavailableError(200, `Unexpected content type ${contentType}`);
  }
  const blob: Blob = response.data;
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new LabelPdfUnavailableError(200, 'Could not read the label bytes'));
    reader.readAsDataURL(blob);
  });
}

interface PrintableLabel {
  shippingLabelId?: number | null;
  labelData?: string | null;
}

interface LabelPrinter {
  printLabelFromUrl(printerId: string, url: string): Promise<{ success: boolean; error?: string }>;
}

/**
 * Prints a stored label through UHB as a data URL. Falls back to printing the ShipStation URL
 * directly only when the server says the stored copy is unavailable (502) or not a PDF (415).
 */
export async function printStoredLabel(
  printer: LabelPrinter,
  printerId: string,
  label: PrintableLabel,
  combined = false
): Promise<{ success: boolean; error?: string }> {
  if (label.shippingLabelId) {
    try {
      const dataUrl = await fetchLabelAsDataUrl(label.shippingLabelId, combined);
      return await printer.printLabelFromUrl(printerId, dataUrl);
    } catch (err) {
      if (!shouldFallBackToDirectUrl(err)) {
        return { success: false, error: err instanceof Error ? err.message : String(err) };
      }
      console.warn('[LabelPdfService] Stored label unavailable, printing the ShipStation URL directly:', err);
    }
  }
  if (label.labelData && label.labelData.startsWith('http')) {
    return await printer.printLabelFromUrl(printerId, label.labelData);
  }
  return { success: false, error: 'No label available to print' };
}
